import { useState, useMemo, useEffect } from "react";
import {
  Icons, PRODUCT_TYPES, PRODUCT_ICON_MAP, PRODUCT_COLOR_MAP,
  PRODUCT_STATUSES,
  INSURANCE_CONTEXT, CAR_RENTAL_CONTEXT, VISA_CONTEXT, CRUISE_CONTEXT,
  VISA_DECISION_STATUSES, PACKAGE_TYPES,
  generateProductId,
  newFlightLeg, newCruiseCabin, newCruisePort, newAdditionalDriver,
  resolveSupplierCommercialRole,
  findSupplierService,
} from "./bookingConstants";
import { useSuppliersList } from "../../api/suppliers/useSuppliersApi";
import { useCheckDuplicateReference } from "../../api/booking/useBookingApi";
import { sortFlightLegs } from "../../utils/flightLegs";
import FlightForm, { hasInvalidBaggage, hasInvalidLegs, hasMissingPnr, legBlockingReasons } from "./FlightForm";
import HotelForm from "./HotelForm";
import TransferForm, { hasInvalidTransfer } from "./TransferForm";
import InsuranceForm from "./InsuranceForm";
import CarRentalForm from "./CarRentalForm";
import VisaForm from "./VisaForm";
import CruiseForm from "./CruiseForm";
import PackageForm from "./PackageForm";
import LoadingButton from "../BaseComponents/LoadingButton/LoadingButton";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import s from "./ProductFormScreen.module.css";

/* ─── Past-date detection ─────────────────────────────────────────────
   Past service dates are ALLOWED (operators register historical bookings),
   but the user gets a confirmation modal before saving. */
const PAST_DATE_FIELDS = {
  Hotel:     [["checkIn", "Check-in"], ["checkOut", "Check-out"]],
  Insurance: [["insuranceStartDate", "Insurance start date"], ["holidayStartDate", "Holiday start date"], ["insuranceExpireDate", "Insurance expiry date"]],
  Package:   [["departureDate", "Departure date"], ["returnDate", "Return date"]],
  CarRental: [["pickupDate", "Pick-up date"], ["dropoffDate", "Drop-off date"]],
  Visa:      [["visaIssueDate", "Visa issue date"], ["visaExpiryDate", "Visa expiry date"]],
  Cruise:    [["departureDate", "Departure date"], ["returnDate", "Return date"]],
};

const collectPastDates = (type, form) => {
  const today = new Date().toISOString().slice(0, 10);
  const isPast = (d) => !!d && /^\d{4}-\d{2}-\d{2}/.test(String(d)) && String(d).slice(0, 10) < today;
  const out = [];
  if (type === "Flight") {
    (form.legs || []).forEach((leg, i) => {
      const lbl = leg.direction || `Segment ${i + 1}`;
      if (isPast(leg.date)) out.push({ label: `${lbl} departure`, date: leg.date });
      if (isPast(leg.arrivalDate)) out.push({ label: `${lbl} arrival`, date: leg.arrivalDate });
    });
  } else if (type === "Transfer") {
    if (isPast(form.pickupDate)) out.push({ label: "Pick-up date", date: form.pickupDate });
    const arrPick = form.arrivalLeg?.pickupDate || form.arrivalLeg?.schedule?.pickupDate;
    const depPick = form.departureLeg?.pickupDate || form.departureLeg?.schedule?.pickupDate;
    if (isPast(arrPick)) out.push({ label: "Arrival transfer pick-up", date: arrPick });
    if (isPast(depPick)) out.push({ label: "Departure transfer pick-up", date: depPick });
  } else {
    (PAST_DATE_FIELDS[type] || []).forEach(([key, label]) => {
      if (isPast(form[key])) out.push({ label, date: form[key] });
    });
  }
  if (isPast(form.serviceDate)) out.push({ label: "Service date", date: form.serviceDate });
  if (isPast(form.serviceDateEnd)) out.push({ label: "Service end date", date: form.serviceDateEnd });
  return out;
};

/* ─── Read-only "within travel booking" notice ───────────────────────
   Shown instead of the context toggle once the booking already holds another
   product — at that point the context is implicitly "within travel booking",
   so there's nothing for the user to choose. */
const WithinTravelNotice = ({ label }) => (
  <div style={{
    display: "inline-flex", alignItems: "center", gap: 8, alignSelf: "flex-start",
    padding: "9px 14px", borderRadius: "var(--radius-sm)",
    background: "linear-gradient(135deg, #fffbf0, #fff4dc)", border: "1.5px solid var(--accent-amber-light, #fde4b0)",
    fontSize: "12.5px", fontWeight: 700, color: "var(--accent-amber, #b45309)",
  }}>
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
    {label}
  </div>
);

/* ─── Shared Context Toggle ───────────────────────────────────────── */
const ContextToggle = ({ value, onChange, options }) => (
  <div style={{ display: "flex", gap: 0, border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "hidden", alignSelf: "flex-start" }}>
    {options.map(m => (
      <button key={m.key} type="button"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px",
          border: "none", background: value === m.key ? "linear-gradient(135deg, #fffbf0, #fff4dc)" : "var(--surface)",
          fontSize: "12.5px", fontWeight: value === m.key ? 700 : 600,
          color: value === m.key ? "var(--accent-amber)" : "var(--text-tertiary)",
          cursor: "pointer", fontFamily: "inherit",
          borderRight: "1px solid var(--border-light)",
          boxShadow: value === m.key ? "inset 0 -2px 0 var(--accent-amber-light)" : "none",
        }}
        onClick={() => onChange(m.key)}
      >
        {value === m.key && (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        )}
        {m.label}
      </button>
    ))}
  </div>
);

// resolveServiceCommercialRole now lives in bookingConstants as
// `resolveSupplierCommercialRole` so Hotel/Flight/Product forms share the
// same logic (handles DEFAULT, BOTH, Flight Service mapping, etc.).
const resolveServiceCommercialRole = resolveSupplierCommercialRole;

/* ─── Supplier Role Badge (read-only) ────────────────────────────── */
const SupplierRoleBadge = ({ role }) => {
  if (!role) return null;
  const labels = { MERCHANT: "Merchant", AGENT: "Agent", BOTH: "Both" };
  const colors = {
    MERCHANT: { bg: "#fef3c7", fg: "#92400e" },
    AGENT: { bg: "#dbeafe", fg: "#1e40af" },
    BOTH: { bg: "#e0e7ff", fg: "#3730a3" },
  };
  const c = colors[role] || colors.MERCHANT;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 999,
      background: c.bg, color: c.fg, letterSpacing: "0.02em",
    }}>
      {labels[role] || role}
    </span>
  );
};

/* ─── Supplier & Status Section (non-Flight products) ─────────────── */
const SupplierStatusSection = ({ type, form, f, hasOtherProducts, supplierRefWarn }) => {
  const { data: suppliers, loading: loadingSuppliers } = useSuppliersList();
  const supplierOptions = useMemo(() =>
    (suppliers || []).filter((sup) => {
      const svc = findSupplierService(sup, type);
      return svc && !!svc.enabled;
    }),
    [suppliers, type]
  );
  const selectedSupplier = form.supplierId ? supplierOptions.find(x => String(x.id) === String(form.supplierId)) : null;
  return (
  <div style={{ background: "#ffffff", border: "1.5px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12, borderBottom: "1.5px solid #eef2f7", marginBottom: 4 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #fef3c7, #fde68a)", color: "#d97706", flexShrink: 0 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#111827", letterSpacing: "0.01em" }}>Supplier & Status</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Supplier <span style={{ color: "#ef4444" }}>*</span></label>
        <select
          style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, color: "var(--text-primary)", background: "var(--surface)", width: "100%", fontFamily: "inherit" }}
          value={form.supplierId || ""}
          onChange={e => {
            const id = e.target.value ? Number(e.target.value) : null;
            const sup = supplierOptions.find(x => String(x.id) === e.target.value);
            f("supplierId", id);
            f("supplier", sup?.name || "");
            f("supplierCommercialRole", sup ? resolveServiceCommercialRole(sup, type) : "MERCHANT");
          }}
        >
          <option value="">{loadingSuppliers ? "Loading suppliers..." : "Select supplier..."}</option>
          {supplierOptions.map(sup => <option key={sup.id} value={sup.id}>{sup.name}{sup.type ? ` (${sup.type})` : ""}</option>)}
        </select>
        {form.supplierId && <SupplierRoleBadge role={form.supplierCommercialRole || (selectedSupplier ? resolveServiceCommercialRole(selectedSupplier, type) : null)} />}
      </div>
      {type === "Insurance" && <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Insurance ID Number</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit", textTransform: "uppercase" }} placeholder="Insurance ID" value={form.insuranceIdNumber} onChange={e => f("insuranceIdNumber", e.target.value.toUpperCase())} /></div>}
      {type === "CarRental" && <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Supplier Reservation Nr</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit", textTransform: "uppercase" }} placeholder="Reservation number" value={form.supplierReservationNumber} onChange={e => f("supplierReservationNumber", e.target.value.toUpperCase())} /></div>}
      {type === "Visa" && <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Supplier Reference Nr</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit", textTransform: "uppercase" }} placeholder="Reference number" value={form.supplierRefNumber} onChange={e => f("supplierRefNumber", e.target.value.toUpperCase())} /></div>}
      {type === "Cruise" && <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Supplier Booking Ref</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit", textTransform: "uppercase" }} placeholder="Booking reference" value={form.supplierRef} onChange={e => f("supplierRef", e.target.value.toUpperCase())} /></div>}
      {type === "Package" && <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Supplier Reservation Nr</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit", textTransform: "uppercase" }} placeholder="Reservation number" value={form.supplierReservationNumber} onChange={e => f("supplierReservationNumber", e.target.value.toUpperCase())} /></div>}
      {type === "Transfer" && <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Supplier Booking Ref</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit", textTransform: "uppercase" }} placeholder="Supplier reference" value={form.supplierRef} onChange={e => f("supplierRef", e.target.value.toUpperCase())} /></div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Product Status</label>
        <select style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit" }} value={form.status} onChange={e => f("status", e.target.value)}>
          {PRODUCT_STATUSES.map(st => <option key={st}>{st}</option>)}
        </select>
      </div>
    </div>
    {supplierRefWarn && (
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", background: "#fffbeb", border: "1px solid #fbbf24", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
        <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
        <span>
          <strong>Duplicate reference found</strong> — this reference already exists in{" "}
          {supplierRefWarn.bookings.map((b, i) => (
            <span key={b.bookingId}>{i > 0 ? ", " : ""}<strong>{b.bookingReference}</strong> ({b.status})</span>
          ))}.
          You can still continue.
        </span>
      </div>
    )}
    {type === "Package" && (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Supplier Invoice Nr</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit", textTransform: "uppercase" }} placeholder="Invoice number" value={form.supplierInvoiceNumber} onChange={e => f("supplierInvoiceNumber", e.target.value.toUpperCase())} /></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Package Type <span style={{ color: "#ef4444" }}>*</span></label><select style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit" }} value={form.packageType} onChange={e => f("packageType", e.target.value)}><option value="">Select type...</option>{PACKAGE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</select></div>
      </div>
    )}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
      {["Package", "Insurance", "CarRental", "Visa", "Cruise"].includes(type)
        ? <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Booking Date</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit" }} type="date" value={form.bookingDate || ""} onChange={e => f("bookingDate", e.target.value)} /></div>
        : <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Creation Date</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit" }} type="date" value={form.creationDate} onChange={e => f("creationDate", e.target.value)} /></div>}
      {["Package", "CarRental", "Visa", "Cruise"].includes(type) && <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Service End Date</label><input style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit" }} type="date" value={form.serviceDateEnd} onChange={e => f("serviceDateEnd", e.target.value)} /></div>}
      <div />
    </div>
    {type === "Insurance" && (hasOtherProducts
      ? <WithinTravelNotice label="Insurance within Travel Booking" />
      : <ContextToggle value={form.insuranceContext} onChange={v => f("insuranceContext", v)} options={INSURANCE_CONTEXT} />)}
    {type === "CarRental" && (hasOtherProducts
      ? <WithinTravelNotice label="Car Rental within Travel Booking" />
      : <ContextToggle value={form.carRentalContext} onChange={v => f("carRentalContext", v)} options={CAR_RENTAL_CONTEXT} />)}
    {type === "Visa" && (
      <>
        {hasOtherProducts
          ? <WithinTravelNotice label="Visa within Travel Booking" />
          : <ContextToggle value={form.visaContext} onChange={v => f("visaContext", v)} options={VISA_CONTEXT} />}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginTop: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Decision Status</label><select style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, width: "100%", fontFamily: "inherit" }} value={form.visaDecisionStatus} onChange={e => f("visaDecisionStatus", e.target.value)}>{VISA_DECISION_STATUSES.map(st => <option key={st}>{st}</option>)}</select></div>
          <div /><div />
        </div>
      </>
    )}
    {type === "Cruise" && (hasOtherProducts
      ? <WithinTravelNotice label="Cruise within Travel Booking" />
      : <ContextToggle value={form.cruiseContext} onChange={v => f("cruiseContext", v)} options={CRUISE_CONTEXT} />)}
  </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Main ProductFormScreen
   ═══════════════════════════════════════════════════════════════════════ */

const ProductFormScreen = ({ type, onClose, onSave, initialData, allProducts = [], saving = false }) => {
  const isEdit = !!initialData;

  /* Derive flight products from all booking products (exclude the one being edited).
     Normalize so legs/flightType/airlinePnr are always top-level (API components
     store them inside _raw.productDetails). */
  const flightProducts = useMemo(() => {
    return (allProducts || [])
      .filter((p) => p.type === "Flight" && (!initialData || p.id !== initialData.id))
      .map((p) => {
        if (p.legs) return { ...p, legs: sortFlightLegs(p.legs) };
        const pd = typeof p._raw?.productDetails === "string"
          ? JSON.parse(p._raw.productDetails)
          : (p._raw?.productDetails || {});
        return {
          ...p,
          legs: sortFlightLegs(pd.legs).map((l) => ({
            id: String(l.id || `leg-${Math.random()}`),
            direction: l.direction || "Outbound",
            departure: l.departureAirport || l.departure || "",
            departureName: l.departureName || "",
            arrival: l.arrivalAirport || l.arrival || "",
            arrivalName: l.arrivalName || "",
            date: l.departureDate || l.date || "",
            time: l.departureTime || l.time || "",
            arrivalDate: l.arrivalDate || "",
            arrivalTime: l.arrivalTime || "",
            airlineCode: l.airlineCode || "",
            airlineName: l.airlineName || "",
            flightNumber: l.flightNumber || "",
          })),
          supplier: p.supplierName || p.supplier || "",
          flightType: pd.flightType || "",
          airlinePnr: pd.airlinePnr || "",
        };
      });
  }, [allProducts, initialData]);

  /* Derive hotel products from all booking products (exclude the one being edited).
     Normalize so hotelName is top-level for booking detail context. */
  const hotelProducts = useMemo(() => {
    return (allProducts || [])
      .filter((p) => p.type === "Hotel" && (!initialData || p.id !== initialData.id))
      .map((p) => {
        let pd = {};
        if (typeof p._raw?.productDetails === "string") { try { pd = JSON.parse(p._raw.productDetails); } catch { pd = {}; } }
        else if (p._raw?.productDetails) pd = p._raw.productDetails;
        // Flat (add-booking) products carry the flag top-level; API components
        // carry it inside productDetails. Absent → treated as "contains transfer".
        const hotelContainsTransfer = p.hotelContainsTransfer ?? pd.hotelContainsTransfer;
        return { ...p, hotelName: p.hotelName || pd.hotelName || "", hotelContainsTransfer };
      });
  }, [allProducts, initialData]);

  /* A Transfer being added while a hotel in this booking is flagged as NOT
     containing transfer → show a (non-blocking) warning. */
  const hotelWithoutTransfer = useMemo(() => {
    if (type !== "Transfer") return false;
    return hotelProducts.some((h) => h.hotelContainsTransfer === false);
  }, [type, hotelProducts]);

  /* When the booking already holds another product, this product is implicitly
     "within travel booking" — the standalone/within context choice is moot. */
  const hasOtherProducts = useMemo(() => {
    return (allProducts || []).some((p) => !initialData || p.id !== initialData.id);
  }, [allProducts, initialData]);

  const buildDefaults = () => {
    const today = new Date().toISOString().slice(0, 10);
    const base = { supplier: "", supplierRef: "", serviceDate: "", serviceDateEnd: "", status: "Pending", notes: "", supplierNote: "", customerRemark: "", creationDate: today };
    const td = {
      Flight: {
        flightBookingMode: "flightonly",
        flightType: "RT",
        flightPricingMode: "FULL",
        airlinePnr: "",
        ticketNumber: "",
        outboundPnr: "",
        outboundTicket: "",
        inboundPnr: "",
        inboundTicket: "",
        legs: [
          { ...newFlightLeg(), direction: "Outbound" },
          { ...newFlightLeg(), direction: "Inbound" },
        ],
      },
      Hotel: { hotelContainsTransfer: true, lastPaymentDate: "", lastPaymentTime: "", lastFreeCancellationDate: "", lastFreeCancellationTime: "", ebFirst_date: "", ebFirst_unit: "%", ebFirst_value: "", ebSecond_date: "", ebSecond_unit: "%", ebSecond_value: "", ebThird_date: "", ebThird_unit: "%", ebThird_value: "", hotelName: "", hotelId: null, hotelAddress: "", checkIn: "", checkOut: "", nights: 1, days: 1, roomRate: "Refundable", roomName: "", roomCharacteristic: "", roomCode: "", roomType: "", numberOfRooms: 1, roomStatus: "", boardCode: "", boardName: "", clientRemarks: "", supplierRemarks: "", officeNotes: "" },
      Transfer: {
        transferContext: "standalone",
        transferMoment: "",
        transferType: "",
        vehicleType: "",
        pickupLocationType: "",
        dropoffLocationType: "",
        pickupLocation: "",
        dropoffLocation: "",
        pickupDate: "",
        pickupTimeType: "exact",
        pickupTime: "",
        numberOfAdults: 1,
        numberOfChildren: 0,
        leadTravelerName: "",
        flightAirlineCode: "",
        flightFlightNumber: "",
        flightDate: "",
        flightTime: "",
        pickupInstructions: "",
        cantFindInstructions: "",
        travelDisruptionInfo: "",
        contactInfoSupplier: "",
        emergencyAssistance: "",
        clientRemarks: "",
        supplierRemarks: "",
        officeNotes: "",
        _linkedFlightId: "",
        _linkedLegId: "",
        arrivalLeg: {},
        departureLeg: {},
      },
      Insurance: { insuranceContext: "Standalone", insuranceType: "", insuranceName: "", insuranceIdNumber: "", coverageArea: "", insuranceStartDate: "", holidayStartDate: "", insuranceExpireDate: "", numberOfPersons: 1, bookingDate: "", invoiceNotes: "", officeNotes: "" },
      Package: { packageType: "", supplierReservationNumber: "", supplierInvoiceNumber: "", bookingDate: "", packageName: "", destinationCity: "", destinationCountry: "", departureDate: "", returnDate: "", numberOfNights: 0, pkgHotelName: "", pkgHotelStars: "", pkgHotelCity: "", pkgHotelCountry: "", pkgNumberOfAdults: 2, pkgNumberOfChildren: 0, pkgNumberOfRooms: 1, pkgRoomType: "", pkgRoomName: "", pkgRoomCharacteristics: "", pkgBoardType: "", pkgDepartureAirport: "", pkgArrivalAirport: "", pkgAirlineName: "", pkgTransferIncluded: false, pkgTransferPriceIncluded: false, pkgInsuranceIncluded: false, pkgInsurancePriceIncluded: false, clientRemarks: "", supplierRemarks: "", officeNotes: "" },
      CarRental: { carRentalContext: "Standalone", supplierReservationNumber: "", bookingDate: "", carCategory: "", vehicleMakeModel: "", orSimilar: false, creditCardRequired: false, depositRequired: false, insuranceIncluded: false, pickupDate: "", pickupTime: "", pickupLocation: "", dropoffDate: "", dropoffTime: "", dropoffLocation: "", rentalDays: 0, leadDriverName: "", leadDriverAge: "", additionalDrivers: [], pickupInstructions: "", supplierContactInfo: "", emergencyAssistance: "", invoiceNotes: "", officeNotes: "" },
      Visa: { visaContext: "Standalone", supplierRefNumber: "", bookingDate: "", visaType: "", visaCountry: "", visaEntryType: "", visaDecisionStatus: "Pending", visaIssueDate: "", visaExpiryDate: "", visaDurationOfStay: "", officeNotes: "" },
      Cruise: { cruiseContext: "Standalone", bookingDate: "", cruiseLine: "", shipName: "", cruiseNameItinerary: "", departurePort: "", arrivalPort: "", departureDate: "", returnDate: "", duration: 0, cabins: [newCruiseCabin()], numberOfAdults: 2, numberOfChildren: 0, leadTravelerName: "", portsOfCall: [newCruisePort()], clientRemarks: "", supplierRemarks: "", officeNotes: "" },
    };
    return { ...base, ...(td[type] || {}), ...(initialData || {}) };
  };

  const [form, setForm] = useState(buildDefaults);
  const f = (key, valOrFn) => setForm(p => ({ ...p, [key]: typeof valOrFn === 'function' ? valOrFn(p[key]) : valOrFn }));

  /* ── Duplicate supplier reference check (real-time, debounced 600ms) ── */
  const { execute: checkDuplicate } = useCheckDuplicateReference();
  const [supplierRefWarn, setSupplierRefWarn] = useState(null);

  const supplierRefValue = form.supplierRef || form.supplierRefNumber || form.supplierReservationNumber || form.insuranceIdNumber || "";

  useEffect(() => {
    if (!supplierRefValue || supplierRefValue.length < 3) { setSupplierRefWarn(null); return; }
    const t = setTimeout(async () => {
      const result = await checkDuplicate("supplier_ref", supplierRefValue);
      setSupplierRefWarn(result.isDuplicate ? result : null);
    }, 600);
    return () => clearTimeout(t);
  }, [supplierRefValue]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Force the "within travel booking" context when another product already
     exists (Insurance/CarRental/Visa/Cruise — Flight & Transfer self-manage). */
  useEffect(() => {
    if (!hasOtherProducts) return;
    const ctxKey = { Insurance: "insuranceContext", CarRental: "carRentalContext", Visa: "visaContext", Cruise: "cruiseContext" }[type];
    if (ctxKey && form[ctxKey] !== "WithinBooking") f(ctxKey, "WithinBooking");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOtherProducts]);

  const canSave =
    !!form.supplier &&
    !(type === "Flight"    && hasInvalidBaggage(form.legs)) &&
    !(type === "Flight"    && hasInvalidLegs(form.legs)) &&
    !(type === "Flight"    && hasMissingPnr(form)) &&
    !(type === "Transfer"  && hasInvalidTransfer(form)) &&
    !(type === "Package"   && !form.packageType);
  // Plain-language reasons Save is off, so the operator is never left guessing
  // which field is blocking them.
  const saveBlockers = [];
  if (!form.supplier) saveBlockers.push("a supplier must be selected");
  if (type === "Flight") {
    saveBlockers.push(...legBlockingReasons(form.legs));
    if (hasInvalidBaggage(form.legs)) {
      saveBlockers.push("a baggage allowance is marked included but is 0 kg / 0 pieces");
    }
    if (hasMissingPnr(form)) saveBlockers.push("the airline PNR is missing");
  }
  if (type === "Transfer" && hasInvalidTransfer(form)) {
    saveBlockers.push("the transfer details are incomplete");
  }
  if (type === "Package" && !form.packageType) saveBlockers.push("a package type must be chosen");

  const selectedPT = PRODUCT_TYPES.find(p => p.key === type);
  const colorTheme = PRODUCT_COLOR_MAP[type] || {};

  const [pastWarn, setPastWarn] = useState(null);

  const doSave = () => {
    // The form groups legs by direction; every reader of the product (overview,
    // assignments, SSRs, pricing, summary) wants them in the order they're flown.
    const saved = type === "Flight" ? { ...form, legs: sortFlightLegs(form.legs) } : form;
    if (isEdit) { onSave({ ...initialData, ...saved, type }); }
    else { onSave({ id: generateProductId(), type, ...saved }); }
    onClose();
  };

  const handleSave = () => {
    const pastDates = collectPastDates(type, form);
    if (pastDates.length > 0) { setPastWarn(pastDates); return; }
    doSave();
  };

  /* ── Render form body based on type ── */
  const renderFormBody = () => {
    if (type === "Flight") return <FlightForm form={form} f={f} hasOtherProducts={hasOtherProducts} supplierRefWarn={supplierRefWarn} />;
    if (type === "Hotel") return <HotelForm form={form} f={f} initialData={initialData} supplierRefWarn={supplierRefWarn} />;
    return (
      <>
        {type === "Transfer" && hotelWithoutTransfer && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", background: "#fff7ed", border: "1.5px solid #fed7aa", borderRadius: "var(--radius-md, 12px)", fontSize: 12.5, color: "#9a3412", lineHeight: 1.55 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>
            </svg>
            <span><strong>This hotel does not include transfer.</strong> The hotel in this booking is marked as not containing transfer. You can still add this transfer, but please confirm it should be booked separately.</span>
          </div>
        )}
        <SupplierStatusSection type={type} form={form} f={f} hasOtherProducts={hasOtherProducts} supplierRefWarn={supplierRefWarn} />
        {type === "Transfer" && <TransferForm form={form} f={f} flightProducts={flightProducts} hotelProducts={hotelProducts} hasOtherProducts={hasOtherProducts} />}
        {type === "Insurance" && <InsuranceForm form={form} f={f} />}
        {type === "CarRental" && <CarRentalForm form={form} f={f} />}
        {type === "Visa" && <VisaForm form={form} f={f} />}
        {type === "Cruise" && <CruiseForm form={form} f={f} />}
        {type === "Package" && <PackageForm form={form} f={f} />}
        {type === "Transfer" && (
          <div style={{ background: "#ffffff", border: "1.5px solid #e5e7eb", borderRadius: "var(--radius-md, 12px)", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12, borderBottom: "1.5px solid #eef2f7", marginBottom: 4 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #f5f3ff, #ede9fe)", color: "#7c3aed", flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary, #111827)", letterSpacing: "0.01em" }}>Notes & Remarks</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Supplier Note</label>
              <textarea style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, fontFamily: "inherit", resize: "vertical", minHeight: 56, width: "100%", boxSizing: "border-box" }} rows={3} value={form.supplierNote} onChange={e => f("supplierNote", e.target.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Customer Remark</label>
              <textarea style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, fontFamily: "inherit", resize: "vertical", minHeight: 56, width: "100%", boxSizing: "border-box" }} rows={3} value={form.customerRemark} onChange={e => f("customerRemark", e.target.value)} />
            </div>
          </div>
        )}
        {!["Insurance", "CarRental", "Visa", "Cruise", "Package"].includes(type) && (
          <div style={{ background: "#ffffff", border: "1.5px solid #e5e7eb", borderRadius: "var(--radius-md, 12px)", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12, borderBottom: "1.5px solid #eef2f7", marginBottom: 4 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #f5f3ff, #ede9fe)", color: "#7c3aed", flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary, #111827)", letterSpacing: "0.01em" }}>Internal Notes</span>
            </div>
            <textarea style={{ padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", fontSize: 13, fontFamily: "inherit", resize: "vertical", minHeight: 56, width: "100%", boxSizing: "border-box" }} placeholder="Add any internal notes about this product..." rows={3} value={form.notes} onChange={e => f("notes", e.target.value)} />
          </div>
        )}
      </>
    );
  };

  const statusColor = form.status === "Confirmed" ? { bg: "#ecfdf5", fg: "#059669" }
    : form.status === "Cancelled" ? { bg: "#fef2f2", fg: "#dc2626" }
    : { bg: "#fffbeb", fg: "#d97706" };

  return (
    <div className={s.formScreen}>
      {/* ── Top Bar ── */}
      <div className={s.topBar}>
        <button className={s.backBtn} onClick={onClose} title="Back to products">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <div className={s.topBarInfo}>
          <div className={s.topBarBreadcrumb}>
            Products
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
            {isEdit ? "Edit" : "Add"} {selectedPT?.label}
          </div>
          <div className={s.topBarTitleRow}>
            <h3 className={s.topBarTitle}>{isEdit ? "Edit" : "New"} {selectedPT?.label}</h3>
            <span className={s.topBarBadge} style={{ background: colorTheme.bg, color: colorTheme.color, borderColor: colorTheme.border }}>
              {PRODUCT_ICON_MAP[type]} {selectedPT?.label}
            </span>
          </div>
        </div>
        <div className={s.topBarActions}>
          <span className={s.unsavedBadge}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
            Unsaved
          </span>
        </div>
      </div>

      {/* ── Product Summary Card ── */}
      <div className={s.summaryCard}>
        <div className={s.summaryIconWrap} style={{ background: colorTheme.bg, color: colorTheme.color, borderColor: colorTheme.border }}>
          {PRODUCT_ICON_MAP[type]}
        </div>
        <div className={s.summaryInfo}>
          <h4 className={s.summaryTitle}>{isEdit ? "Edit" : "New"} {selectedPT?.label}</h4>
          <div className={s.summaryMeta}>
            {form.supplier && (
              <span className={s.summaryMetaItem}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {form.supplier}
              </span>
            )}
            {form.creationDate && (
              <span className={s.summaryMetaItem}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {form.creationDate}
              </span>
            )}
            {form.supplierRef && (
              <span className={s.summaryMetaItem}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
                Ref: {form.supplierRef}
              </span>
            )}
          </div>
        </div>
        <span className={s.summaryStatusBadge} style={{ background: statusColor.bg, color: statusColor.fg }}>
          {form.status || "Pending"}
        </span>
      </div>

      {/* ── Form Content ── */}
      <div className={s.formBody}>
        {renderFormBody()}
      </div>

      {/* ── Sticky Bottom Action Bar ── */}
      <div className={s.actionBar}>
        {/* Say WHY Save is off. Field errors only appear once a field is
            touched, which never happens on an edit form nobody typed in — so a
            leg missing an airline name disabled the button with nothing on
            screen to explain it. "Required fields must be filled" is not an
            answer when every field you can see looks filled. */}
        {!canSave ? (
          <span className={s.blockedHint} title={saveBlockers.join("\n")}>
            <span className={s.reqDot} />
            {saveBlockers.length
              ? `Cannot save — ${saveBlockers[0]}${saveBlockers.length > 1 ? ` (+${saveBlockers.length - 1} more)` : ""}`
              : "Required fields must be filled"}
          </span>
        ) : (
          <span className={s.reqHint}>
            <span className={s.reqDot} />
            Required fields must be filled
          </span>
        )}
        <button className={s.btnSecondary} disabled={saving} onClick={onClose}>Cancel</button>
        <LoadingButton
          className={`${s.btnPrimary} ${!canSave ? s.btnDisabled : ""}`}
          disabled={!canSave}
          loading={saving}
          loadingText={isEdit ? "Saving…" : "Adding…"}
          icon={isEdit ? Icons.check : Icons.plus}
          onClick={handleSave}
        >
          {isEdit ? "Save Changes" : "Add Product"}
        </LoadingButton>
      </div>

      {/* ── Past-date confirmation ── */}
      <BaseModal open={!!pastWarn} onClose={() => setPastWarn(null)} title="Past Date Selected" width="480px">
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "12px 14px" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>
            </svg>
            <div style={{ fontSize: 13, color: "#9a3412", lineHeight: 1.55 }}>
              You have selected a past date. The product will be saved as a <strong>historical booking</strong>. Do you want to continue?
            </div>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#374151", display: "flex", flexDirection: "column", gap: 4 }}>
            {(pastWarn || []).map((p, i) => (
              <li key={i}><strong>{p.label}:</strong> {p.date}</li>
            ))}
          </ul>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
            <button
              type="button"
              onClick={() => setPastWarn(null)}
              style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Go Back
            </button>
            <button
              type="button"
              onClick={() => { setPastWarn(null); doSave(); }}
              style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "var(--primary-color, #f5a51e)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
            >
              Continue
            </button>
          </div>
        </div>
      </BaseModal>
    </div>
  );
};

export default ProductFormScreen;